/**
 * Record despatched, from our warehouse (order to invoice doc 102 §4.1, §9.2):
 * one consignment — one date, carrier and tracking reference — to one
 * customer address. This order's lines in the delivery group go ticked, each
 * with how many went; other orders' lines ready for the same address are
 * offered unticked, so what leaves in one box is recorded once. Each order
 * keeps its own delivery charge.
 *
 * The lines, what is still to go and the URL are the order view's
 * `record_despatched_together`; the carrier list is the one every despatch
 * form uses. The dialog makes one consignment key when it opens and resends
 * it on a retry, so a retry records once. `onRecord` resolves to the
 * refusal to show, or null once recorded.
 *
 * With DPD or DX chosen where CIS books with them (courier chain plan 01),
 * the dialog offers *Book with … and print the label*: the parcels prefilled
 * from the stock items' weights, the courier's services (by 12 the default),
 * and the booking recorded as the despatch under the courier's number.
 */
import { useEffect, useId, useState } from "react";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import type {
  BookCourierDespatchRequest,
  ConsignmentLine,
  CourierBookingPrefill,
  DeliveryGroupView,
  RecordDespatchedTogether,
  RecordDespatchedTogetherRequest,
} from "@shared-core/data/order-to-invoice-types";
import type { DryRun } from "@shared-core/domain/object-ui/contract";
import {
  CARRIERS,
  type Carrier,
} from "@shared-core/data/supplier-portal-adapter";
import { carrierLabel } from "@shared-core/domain/supplier-portal-wording";
import {
  londonTodayIso,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Field, FailureBanner } from "./OrderDialogs";

const count = (n: number) => n.toLocaleString("en-GB");

/** A consignment line's place in the dialog: its order and delivery line. */
function keyOf(line: ConsignmentLine): string {
  return `${line.order_no}|${line.delivery_line_id}`;
}

interface Pick {
  isTicked: boolean;
  quantity: string;
}

/** The checks made as typed on one ticked line: a whole number, no more than is still to go. */
export function consignmentQuantityProblem(
  quantity: string,
  outstanding: number,
): string | undefined {
  const parsed = Number(quantity);
  if (quantity.trim() === "" || !Number.isInteger(parsed) || parsed <= 0)
    return "Enter a whole number above 0.";
  if (parsed > outstanding)
    return `Only ${count(outstanding)} are outstanding on this line.`;
  return undefined;
}

/** "From Pollards Bristol to Acme Leeds HQ, next day." */
export function consignmentDescription(group: DeliveryGroupView): string {
  const from = group.origin?.name ? `From ${group.origin.name} to` : "To";
  const level = group.service_level
    ? `, ${group.service_level.charAt(0).toLowerCase()}${group.service_level.slice(1)}`
    : "";
  return `${from} ${group.destination_name}${level}.`;
}

/** The carriers CIS can book with (each has a booking adapter under its carrier code). */
const BOOKABLE: ReadonlySet<string> = new Set(["dpd", "dx"]);
const MAX_PARCEL_KG = 30;

/** The check made as typed on one parcel's weight: a number above 0, at most 30 kg on a parcel service. */
export function parcelWeightProblem(weight: string): string | undefined {
  const parsed = Number(weight);
  if (weight.trim() === "" || !Number.isFinite(parsed) || parsed <= 0)
    return "Enter the weight in kg.";
  if (parsed > MAX_PARCEL_KG)
    return `A parcel is at most ${MAX_PARCEL_KG} kg: split it.`;
  return undefined;
}

export function RecordDespatchedTogetherDialog({
  group,
  together,
  busy,
  onClose,
  onRecord,
  onPreviewBooking,
  onBook,
}: {
  /** The delivery group it was opened from; its lines are "This order". */
  group: DeliveryGroupView;
  together: RecordDespatchedTogether;
  busy: boolean;
  onClose: () => void;
  onRecord: (
    input: RecordDespatchedTogetherRequest,
  ) => Promise<OrderStepFailure | null>;
  /**
   * *Book with DPD / DX*'s dry run (courier chain plan 01): the carriers bookable here, the parcels prefilled from the
   * stock items' weights and the courier's services. Absent (with `onBook`) where the order offers no booking URL.
   */
  onPreviewBooking?: (input: BookCourierDespatchRequest) => Promise<DryRun>;
  /** Book the consignment and print its label; resolves to the refusal to show, or null once booked. */
  onBook?: (
    input: BookCourierDespatchRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const today = londonTodayIso();
  const [requestId] = useState(newUuid);
  const [on, setOn] = useState(today);
  const [carrier, setCarrier] = useState<Carrier | "">("");
  const [carrierName, setCarrierName] = useState("");
  const [tracking, setTracking] = useState("");
  const [picks, setPicks] = useState<Record<string, Pick>>(() =>
    Object.fromEntries([
      ...together.lines.map((l) => [
        keyOf(l),
        { isTicked: true, quantity: String(l.outstanding_quantity) },
      ]),
      ...together.ready_for_same_address.map((l) => [
        keyOf(l),
        { isTicked: false, quantity: String(l.outstanding_quantity) },
      ]),
    ]),
  );
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const [itemKeys, setItemKeys] = useState<string[]>([]);

  // Booking with the courier (courier chain plan 01): offered when the order carries a booking URL and the carrier
  // chosen is one CIS books with here; on by default then, off to type a tracking number instead.
  const canBook = !!together.book_courier_url && !!onPreviewBooking && !!onBook;
  const [bookableCarriers, setBookableCarriers] = useState<string[]>([]);
  const [isBooking, setIsBooking] = useState(true);
  const [parcels, setParcels] = useState<string[]>([]);
  const [parcelsEdited, setParcelsEdited] = useState(false);
  const [services, setServices] = useState<{ code: string; name: string }[]>(
    [],
  );
  const [serviceCode, setServiceCode] = useState("");
  const [contents, setContents] = useState("Printed matter");
  const [previewNote, setPreviewNote] = useState<string | null>(null);

  const allLines = [...together.lines, ...together.ready_for_same_address];
  const ticked = allLines.filter((l) => picks[keyOf(l)]?.isTicked);
  const items = ticked.map((l) => ({
    order_no: l.order_no,
    line_ref: l.line_ref,
    delivery_line_id: l.delivery_line_id,
    quantity: Number(picks[keyOf(l)]!.quantity),
  }));
  const bookingHere =
    canBook && !!carrier && bookableCarriers.includes(carrier);
  const booking = bookingHere && isBooking;
  // What the dry run depends on: re-asked when the carrier or the ticked lines change.
  const previewKey = JSON.stringify([carrier, items]);

  useEffect(() => {
    if (!canBook || !onPreviewBooking) return;
    let stale = false;
    onPreviewBooking({
      despatched_on: on,
      carrier:
        carrier && BOOKABLE.has(carrier) ? (carrier as "dpd" | "dx") : null,
      service_code: null,
      parcels: [],
      contents: null,
      items,
      request_id: null,
    })
      .then((dry) => {
        if (stale) return;
        const prefill = (dry.prefill ?? {}) as Partial<CourierBookingPrefill>;
        const carriers = dry.choices.find((c) => c.key === "carrier");
        setBookableCarriers(carriers?.options.map((o) => o.value) ?? []);
        if (prefill.parcels && !parcelsEdited)
          setParcels(prefill.parcels.map((p) => String(p.weight_kg)));
        const offered = dry.choices.find((c) => c.key === "service_code");
        if (offered)
          setServices(
            offered.options.map((o) => ({ code: o.value, name: o.label })),
          );
        if (prefill.service_code) setServiceCode(prefill.service_code);
        const problem = dry.checks.find((c) => c.state !== "pass");
        setPreviewNote(problem ? problem.label : null);
      })
      .catch(() => {
        if (!stale) setPreviewNote("The courier could not be asked just now.");
      });
    return () => {
      stale = true;
    };
    // `items` is what previewKey serialises; `on` and `parcelsEdited` only matter alongside a change in them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBook, onPreviewBooking, previewKey]);

  const quantityProblems = Object.fromEntries(
    ticked.map((l) => [
      keyOf(l),
      consignmentQuantityProblem(
        picks[keyOf(l)]!.quantity,
        l.outstanding_quantity,
      ),
    ]),
  );
  const nothingTicked =
    ticked.length === 0 ? "Tick at least one line to record a despatch." : null;
  const dateProblem = !on
    ? "Enter a date."
    : on > today
      ? "The date can't be in the future."
      : undefined;
  const parcelProblems = booking ? parcels.map(parcelWeightProblem) : [];
  const bookingProblem = booking
    ? parcels.length === 0
      ? "Add at least one parcel."
      : !serviceCode
        ? "Choose the courier's service."
        : undefined
    : undefined;
  const hasProblem =
    !!nothingTicked ||
    !!dateProblem ||
    Object.values(quantityProblems).some(Boolean) ||
    (carrier === "other" && carrierName.trim() === "") ||
    parcelProblems.some(Boolean) ||
    !!bookingProblem;
  // The server's refusal of one ticked line, back on that line's input.
  const serverQuantityError = (key: string) => {
    const index = itemKeys.indexOf(key);
    return index < 0
      ? undefined
      : failure?.fieldErrors[`items.${index}.quantity`];
  };

  const setPick = (key: string, change: Partial<Pick>) =>
    setPicks((current) => ({
      ...current,
      [key]: { ...current[key]!, ...change },
    }));
  const setParcel = (index: number, weight: string) => {
    setParcelsEdited(true);
    setParcels((current) => current.map((w, i) => (i === index ? weight : w)));
  };

  const lineRows = (lines: ConsignmentLine[], showOrder: boolean) =>
    lines.map((line) => {
      const key = keyOf(line);
      const pick = picks[key]!;
      const label = `${showOrder ? `${line.order_no} ` : ""}${line.line_no} ${line.title}`;
      const quantityError = pick.isTicked
        ? (quantityProblems[key] ?? serverQuantityError(key))
        : undefined;
      const inputId = `${id}-${key}-quantity`;
      return (
        <li key={key} className="space-y-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
              <Checkbox
                checked={pick.isTicked}
                onCheckedChange={(c) => setPick(key, { isTicked: c === true })}
                aria-label={`Send ${label}`}
              />
              <span className="min-w-0 truncate">{label}</span>
            </label>
            <span className="text-xs tabular-nums text-muted-foreground">
              {count(line.outstanding_quantity)} of {count(line.quantity)} to
              send
            </span>
            <Input
              id={inputId}
              inputMode="numeric"
              className="h-8 w-24"
              aria-label={`How many of ${label}`}
              value={pick.quantity}
              disabled={!pick.isTicked}
              onChange={(e) => setPick(key, { quantity: e.target.value })}
              aria-invalid={quantityError ? true : undefined}
              aria-describedby={quantityError ? `${inputId}-error` : undefined}
            />
          </div>
          {quantityError && (
            <p id={`${inputId}-error`} className="text-sm text-red-600">
              {quantityError}
            </p>
          )}
        </li>
      );
    });

  const carrierWord = carrier ? carrierLabel(carrier) : "";
  const submitLabel = booking
    ? `Book with ${carrierWord} and print label`
    : "Record despatched";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Record despatched</DialogTitle>
          <DialogDescription>{consignmentDescription(group)}</DialogDescription>
        </DialogHeader>
        {/* noValidate: each check says why beside its field, where the browser's `max` would only block. */}
        <form
          noValidate
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setFailure(null);
            if (hasProblem) return;
            setItemKeys(ticked.map(keyOf));
            const refused = booking
              ? await onBook!({
                  despatched_on: on,
                  request_id: requestId,
                  carrier: carrier as "dpd" | "dx",
                  service_code: serviceCode,
                  parcels: parcels.map((w) => ({ weight_kg: Number(w) })),
                  contents: contents.trim() || null,
                  items,
                })
              : await onRecord({
                  despatched_on: on,
                  request_id: requestId,
                  carrier: carrier || null,
                  carrier_name: carrier === "other" ? carrierName.trim() : null,
                  tracking_number:
                    carrier && carrier !== "own_van"
                      ? tracking.trim() || null
                      : null,
                  items,
                });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <div className="grid gap-3 sm:grid-cols-3">
            <Field
              id={`${id}-on`}
              label="Despatched on"
              error={dateProblem ?? failure?.fieldErrors.despatched_on}
            >
              {(aria) => (
                <Input
                  {...aria}
                  type="date"
                  max={today}
                  value={on}
                  onChange={(e) => setOn(e.target.value)}
                />
              )}
            </Field>
            <Field
              id={`${id}-carrier`}
              label="Carrier"
              error={failure?.fieldErrors.carrier}
            >
              {(aria) => (
                <Select
                  value={carrier}
                  onValueChange={(value) => setCarrier(value as Carrier)}
                >
                  <SelectTrigger {...aria} className="w-full">
                    <SelectValue placeholder="Choose a carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {CARRIERS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {carrierLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field
              id={`${id}-tracking`}
              label="Tracking reference"
              error={failure?.fieldErrors.tracking_number}
              hint={
                booking ? `${carrierWord} gives it when booked.` : undefined
              }
            >
              {(aria) => (
                <Input
                  {...aria}
                  value={tracking}
                  maxLength={64}
                  autoComplete="off"
                  disabled={!carrier || carrier === "own_van" || booking}
                  onChange={(e) => setTracking(e.target.value)}
                />
              )}
            </Field>
          </div>
          {carrier === "other" && (
            <Field
              id={`${id}-carrier-name`}
              label="Carrier's name"
              error={
                failure?.fieldErrors.carrier_name ??
                (carrierName.trim() === "" ? "Name the carrier." : undefined)
              }
            >
              {(aria) => (
                <Input
                  {...aria}
                  value={carrierName}
                  maxLength={100}
                  onChange={(e) => setCarrierName(e.target.value)}
                />
              )}
            </Field>
          )}
          {bookingHere && (
            <section
              className="space-y-3 rounded-md border p-3"
              aria-labelledby={`${id}-booking`}
            >
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={isBooking}
                  onCheckedChange={(c) => setIsBooking(c === true)}
                  aria-label={`Book with ${carrierWord} and print the label`}
                />
                <span id={`${id}-booking`}>
                  Book with {carrierWord} and print the label
                </span>
              </label>
              {booking && (
                <>
                  <Field
                    id={`${id}-service`}
                    label="Service"
                    error={
                      failure?.fieldErrors.service_code ??
                      (serviceCode ? undefined : bookingProblem)
                    }
                  >
                    {(aria) => (
                      <Select
                        value={serviceCode}
                        onValueChange={setServiceCode}
                      >
                        <SelectTrigger {...aria} className="w-full">
                          <SelectValue placeholder="Choose a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (
                            <SelectItem key={s.code} value={s.code}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">
                      Parcels{" "}
                      <span className="font-normal text-muted-foreground">
                        (weight in kg, from the stock items; correct as packed)
                      </span>
                    </legend>
                    {parcels.length === 0 && (
                      <p className="text-sm text-red-600" role="alert">
                        Add at least one parcel.
                      </p>
                    )}
                    <ul className="space-y-1">
                      {parcels.map((weight, index) => {
                        const parcelId = `${id}-parcel-${index}`;
                        const problem =
                          parcelProblems[index] ??
                          failure?.fieldErrors[`parcels.${index}.weight_kg`];
                        return (
                          <li key={parcelId} className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="w-20 text-sm">
                                Parcel {index + 1}
                              </span>
                              <Input
                                id={parcelId}
                                inputMode="decimal"
                                className="h-8 w-28"
                                aria-label={`Parcel ${index + 1} weight in kg`}
                                value={weight}
                                onChange={(e) =>
                                  setParcel(index, e.target.value)
                                }
                                aria-invalid={problem ? true : undefined}
                                aria-describedby={
                                  problem ? `${parcelId}-error` : undefined
                                }
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                aria-label={`Remove parcel ${index + 1}`}
                                onClick={() => {
                                  setParcelsEdited(true);
                                  setParcels((current) =>
                                    current.filter((_, i) => i !== index),
                                  );
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                            {problem && (
                              <p
                                id={`${parcelId}-error`}
                                className="text-sm text-red-600"
                              >
                                {problem}
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setParcelsEdited(true);
                        setParcels((current) => [
                          ...current,
                          current[current.length - 1] ?? "",
                        ]);
                      }}
                    >
                      Add a parcel
                    </Button>
                  </fieldset>
                  <Field
                    id={`${id}-contents`}
                    label="Contents on the label"
                    error={failure?.fieldErrors.contents}
                  >
                    {(aria) => (
                      <Input
                        {...aria}
                        value={contents}
                        maxLength={25}
                        onChange={(e) => setContents(e.target.value)}
                      />
                    )}
                  </Field>
                  {previewNote && (
                    <p className="text-sm text-amber-700" role="status">
                      {previewNote}
                    </p>
                  )}
                </>
              )}
            </section>
          )}
          <section className="space-y-2" aria-labelledby={`${id}-this-order`}>
            <h3 id={`${id}-this-order`} className="text-sm font-medium">
              This order
            </h3>
            <ul className="space-y-2">{lineRows(together.lines, false)}</ul>
          </section>
          {together.ready_for_same_address.length > 0 && (
            <section className="space-y-2" aria-labelledby={`${id}-ready`}>
              <h3 id={`${id}-ready`} className="text-sm font-medium">
                Ready for the same address
              </h3>
              <ul className="space-y-2">
                {lineRows(together.ready_for_same_address, true)}
              </ul>
              <p className="text-xs text-muted-foreground">
                Sent together: each order keeps its own delivery charge.
              </p>
            </section>
          )}
          {nothingTicked && (
            <p role="alert" className="text-sm text-red-600">
              {nothingTicked}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || hasProblem}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The toast once recorded: names the other order that went in the same consignment, or counts several. */
export function despatchedTogetherToast(
  orderNo: string,
  input: RecordDespatchedTogetherRequest,
): string {
  const others = [
    ...new Set(
      (input.items ?? []).map((i) => i.order_no).filter((no) => no !== orderNo),
    ),
  ];
  if (others.length === 0) return "Despatch recorded.";
  return others.length === 1
    ? `Despatch recorded for this order and ${others[0]}.`
    : `Despatch recorded for this order and ${others.length} others.`;
}
